import _ from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getClients } from 'common-src/models/Client';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';
import { getAthenaAppointmentVisits } from 'common-src/models/PatientIntegrationsAthena';
import {
  getPatientOncehubBookings,
  getPatientOncehubBookingsRequest,
} from 'common-src/models/PatientOncehubBooking';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import { RequestHandlerScreen } from 'src/components/base';
import { TabsViewNew } from 'src/components/tabs';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useInterval from 'src/hooks/useInterval';
import { useScheduledCallsBanner } from 'src/hooks/useScheduledCallsBanner';

import { DefaultTimezone, ScheduledCallTabs } from './constants';
import { canceledFilter } from './helpers';
import InfoBar from './InfoBar';
import styles from './ScheduledCallsFragment.module.scss';
import ScheduledCallsList from './ScheduledCallsList';

const SCHEDULED_CALLS_POLLING_FREQUENCY = 10000;

const ScheduledCallsFragment = ({ patientId }) => {
  const dispatch = useDispatch();
  const clients = useCustomSelector((state) => getClients(state));

  const scheduledCallKeys = Object.keys(ScheduledCallTabs);
  const [selectedTab, setSelectedTab] = useState(scheduledCallKeys[0]);
  const [tabInfo, setTabInfo] = useState({});
  const [filteredOncehubBookings, setFilteredOncehubBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [athenaAppointments, setAthenaAppointments] = useState([]);
  const [isLoadingAthena, setIsLoadingAthena] = useState(false);

  const oncehubBookings = useCustomSelector((state) =>
    getPatientOncehubBookings(state, patientId ?? null),
  );
  const patient = useCustomSelector((state) => singleModelSelector(state, 'Patient', patientId));
  const isAthenaEnabled = useCustomSelector((state) => isFeatureEnabled(state, 'athena'));
  const timeZone = patient?.timeZone ?? DefaultTimezone;

  const transformAthenaAppointments = () =>
    athenaAppointments.map((apt) => {
      // startTime is now a datetime from the database
      const appointmentDate = apt.sourcePayload?.date || '';
      const appointmentTime = apt.sourcePayload?.starttime || '';
      let scheduledAt = null;
      if (appointmentDate && appointmentTime) {
        scheduledAt = moment
          .tz(`${appointmentDate} ${appointmentTime}`, 'MM/DD/YYYY HH:mm', timeZone)
          .toISOString();
      }
      const numericId = -parseInt(apt.athenaAppointmentId, 10);

      return {
        _fields: {
          id: numericId,
          name: null,
          eventType: null,
          eventName: apt.appointmentType || 'Athena Appointment',
          scheduledAt,
          createdAt: apt.sourcePayload?.scheduleddatetime
            ? moment(apt.sourcePayload.scheduleddatetime, 'MM/DD/YYYY HH:mm:ss').toISOString()
            : null,
          updatedAt: apt.sourcePayload?.lastmodified
            ? moment(apt.sourcePayload.lastmodified, 'MM/DD/YYYY HH:mm:ss').toISOString()
            : null,
          updatedBy: null,
          durationMinutes: apt.sourcePayload?.duration || 30,
          rescheduleUrl: null,
          visitType: null,
          visitReason: apt.reason || apt.sourcePayload?.appointmentnotes?.[0]?.text || '-',
          status: apt.statusRaw === 'x' ? 'canceled' : 'scheduled',
          clientId: null,
          patientId,
        },
        clientId: null,
        patientId,
        isAthenaAppointment: true,
        athenaData: apt,
      };
    });

  const getFilteredOncehubBookings = () => {
    // Get OnceHub bookings based on selected tab (original logic)
    let oncehubFiltered = [];
    switch (selectedTab) {
      case scheduledCallKeys[0]: {
        oncehubFiltered =
          oncehubBookings?.filter(
            (b) =>
              !moment().tz(timeZone).isAfter(moment(b.scheduledAt).tz(timeZone)) &&
              !canceledFilter(b),
          ) || [];
        break;
      }
      case scheduledCallKeys[1]: {
        oncehubFiltered =
          oncehubBookings?.filter(
            (b) =>
              moment().tz(timeZone).isAfter(moment(b.scheduledAt).tz(timeZone)) &&
              !canceledFilter(b),
          ) || [];
        break;
      }
      case scheduledCallKeys[2]: {
        oncehubFiltered = oncehubBookings?.filter((b) => canceledFilter(b)) || [];
        break;
      }
      case scheduledCallKeys[3]: {
        oncehubFiltered = oncehubBookings || [];
        break;
      }
      default: {
        oncehubFiltered = [];
      }
    }

    // Add Athena appointments based on selected tab
    const transformedAthenaAppts = transformAthenaAppointments();
    let athenaFiltered = [];

    switch (selectedTab) {
      case scheduledCallKeys[0]: {
        // Upcoming
        athenaFiltered = transformedAthenaAppts.filter(
          (b) =>
            b._fields?.scheduledAt &&
            !moment().tz(timeZone).isAfter(moment(b._fields.scheduledAt).tz(timeZone)) &&
            b._fields?.status !== 'canceled',
        );
        break;
      }
      case scheduledCallKeys[1]: {
        // Past
        athenaFiltered = transformedAthenaAppts.filter(
          (b) =>
            b._fields?.scheduledAt &&
            moment().tz(timeZone).isAfter(moment(b._fields.scheduledAt).tz(timeZone)) &&
            b._fields?.status !== 'canceled',
        );
        break;
      }
      case scheduledCallKeys[2]: {
        // Canceled
        athenaFiltered = transformedAthenaAppts.filter((b) => b._fields?.status === 'canceled');
        break;
      }
      case scheduledCallKeys[3]: {
        // All Visits
        athenaFiltered = transformedAthenaAppts;
        break;
      }
      default: {
        athenaFiltered = [];
      }
    }

    return [...oncehubFiltered, ...athenaFiltered];
  };

  useInterval(() => {
    dispatch(getPatientOncehubBookingsRequest({ patientId, sort_order: 'desc', limit: 5 }));
  }, SCHEDULED_CALLS_POLLING_FREQUENCY);

  const { showBanner, warningMessage } = useScheduledCallsBanner(
    oncehubBookings,
    timeZone,
    !patientId,
  );

  useEffect(() => {
    const transformedAthenaAppts = transformAthenaAppointments();
    const allVisits = [...(oncehubBookings || []), ...transformedAthenaAppts];

    const upcomingCount = allVisits.filter((b) => {
      const scheduledAt = b.isAthenaAppointment ? b._fields?.scheduledAt : b.scheduledAt;
      const status = b.isAthenaAppointment ? b._fields?.status : b.status;
      return (
        scheduledAt &&
        !moment().tz(timeZone).isAfter(moment(scheduledAt).tz(timeZone)) &&
        status !== 'canceled' &&
        !canceledFilter(b)
      );
    }).length;

    const pastCount = allVisits.filter((b) => {
      const scheduledAt = b.isAthenaAppointment ? b._fields?.scheduledAt : b.scheduledAt;
      const status = b.isAthenaAppointment ? b._fields?.status : b.status;
      return (
        scheduledAt &&
        moment().tz(timeZone).isAfter(moment(scheduledAt).tz(timeZone)) &&
        status !== 'canceled' &&
        !canceledFilter(b)
      );
    }).length;

    const canceledCount = allVisits.filter((b) => {
      const status = b.isAthenaAppointment ? b._fields?.status : b.status;
      return status === 'canceled' || canceledFilter(b);
    }).length;

    setTabInfo({
      UPCOMING: {
        text: ScheduledCallTabs.UPCOMING.text,
        count: upcomingCount,
      },
      PAST: {
        text: ScheduledCallTabs.PAST.text,
        count: pastCount,
      },
      CANCELED: {
        text: ScheduledCallTabs.CANCELED.text,
        count: canceledCount,
      },
      ALL: {
        text: ScheduledCallTabs.ALL.text,
        count: allVisits.length,
      },
    });

    setFilteredOncehubBookings(getFilteredOncehubBookings());
  }, [oncehubBookings, athenaAppointments]);

  useEffect(() => {
    setFilteredOncehubBookings(getFilteredOncehubBookings());
  }, [selectedTab]);

  useEffect(() => {
    if (!patientId && _.isEmpty(clients)) return;

    dispatch(
      getPatientOncehubBookingsRequest({ patientId }, { successBlock: () => setIsLoading(false) }),
    );
  }, [clients, patientId]);

  useEffect(() => {
    const fetchAthenaAppointments = () => {
      if (!isAthenaEnabled || !patientId) {
        setAthenaAppointments([]);
        return;
      }

      setIsLoadingAthena(true);

      dispatch(
        getAthenaAppointmentVisits(patientId, {
          successBlock: (data) => {
            setAthenaAppointments(Array.isArray(data) ? data : []);
            setIsLoadingAthena(false);
          },
          errorBlock: () => {
            setAthenaAppointments([]);
            setIsLoadingAthena(false);
          },
        }),
      );
    };

    fetchAthenaAppointments();
  }, [patientId, isAthenaEnabled]);

  if (isLoading) {
    return <RequestHandlerScreen isRequesting requestingText="Pulling scheduled calls..." />;
  }

  return (
    <section className={[styles.container, 'flex-column'].join(' ')}>
      {showBanner && (
        <p
          className={[
            styles.warningBanner,
            'vertically-centered',
            'horizontally-centered',
            'font-s-14',
          ].join(' ')}
        >
          {warningMessage}
        </p>
      )}
      {patientId && <InfoBar patientId={patientId} />}
      <TabsViewNew
        classNames={[styles.tabs, 'gap-30']}
        options={tabInfo}
        onTabSelected={setSelectedTab}
      />
      <ScheduledCallsList scheduledCalls={filteredOncehubBookings} patientId={patientId} />
    </section>
  );
};

ScheduledCallsFragment.propTypes = {
  patientId: PropTypes.number,
};

export default ScheduledCallsFragment;
